import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

// A "shopping list" of warehouse items to re-order, keyed by item id, each
// with a quantity - a parallel buffer to WarehouseLabelBuffer.jsx (same
// shape, same API), kept as a separate context rather than a mode of that
// one so a user can build a print batch and a shopping list at the same
// time. See that file for the sessionStorage/pruning rationale, which
// applies identically here.
const STORAGE_KEY = "warehouseShoppingList";

const WarehouseShoppingListBufferContext = createContext(null);

function loadInitial() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function WarehouseShoppingListBufferProvider({ children }) {
  const [buffer, setBuffer] = useState(loadInitial);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  }, [buffer]);

  const isBuffered = (itemId) => Boolean(buffer[itemId]);

  const toggle = (item) => {
    setBuffer((prev) => {
      if (prev[item.id]) {
        const next = { ...prev };
        delete next[item.id];
        return next;
      }
      return { ...prev, [item.id]: { item, quantity: 1 } };
    });
  };

  const remove = (itemId) => {
    setBuffer((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const setQuantity = (itemId, quantity) => {
    setBuffer((prev) => {
      if (!prev[itemId]) return prev;
      if (quantity <= 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: { ...prev[itemId], quantity } };
    });
  };

  const clear = () => setBuffer({});

  // Items are clinic-scoped; a master browsing a different clinic than
  // whatever's currently buffered would otherwise be able to build a
  // cross-clinic shopping list. Called by the warehouse pages on mount.
  const pruneToClinicId = (clinicId) => {
    setBuffer((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(
          ([, entry]) => entry.item.clinicId === clinicId,
        ),
      );
      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  };

  const entries = useMemo(() => Object.values(buffer), [buffer]);

  const value = {
    entries,
    count: entries.length,
    isBuffered,
    toggle,
    remove,
    setQuantity,
    clear,
    pruneToClinicId,
  };

  return (
    <WarehouseShoppingListBufferContext.Provider value={value}>
      {children}
    </WarehouseShoppingListBufferContext.Provider>
  );
}

export function useWarehouseShoppingListBuffer() {
  const context = useContext(WarehouseShoppingListBufferContext);
  if (!context) {
    throw new Error(
      "useWarehouseShoppingListBuffer must be used within a WarehouseShoppingListBufferProvider",
    );
  }
  return context;
}
